import React, { useState } from 'react';
import { CheckCircle, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { ResourceMatchResult, MatchSignal } from '@/types/recommendation';
import { Button } from '@/components/ui/button';
import { ResourceIdentityBlock } from './ResourceIdentityBlock';
import { MatchScoreBadge } from './MatchScoreBadge';
import { AvailabilityWindow } from './AvailabilityWindow';
import { ConflictSummaryAlert } from './ConflictSummaryAlert';
import { cn } from '@/lib/utils';
import { useRagThresholds, getRagAccent } from '@/hooks/useRagThresholds';
import { Progress } from '@/components/ui/progress';

interface ConflictResourceCardProps {
  result: ResourceMatchResult;
  requestedStart: Date;
  requestedEnd: Date;
  requiredSkills?: string[];
  isSelected?: boolean;
  onToggle?: (result: ResourceMatchResult) => void;
}

function SignalDetailRow({ signal }: { signal: MatchSignal }) {
  const thresholds = useRagThresholds();
  const accent = getRagAccent(signal.score, thresholds);

  const scoreColour =
    accent === 'success'
      ? 'text-emerald-600'
      : accent === 'warning'
      ? 'text-amber-500'
      : 'text-destructive';

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-foreground">{signal.label}</span>
        <span className={cn('text-xs font-bold tabular-nums', scoreColour)}>
          {signal.score}%
        </span>
      </div>
      <Progress
        variant="mini"
        value={signal.score}
        accent={accent}
        aria-label={`${signal.label}: ${signal.score}%`}
      />
      {signal.explanation && (
        <p className="text-xs text-muted-foreground leading-snug">{signal.explanation}</p>
      )}
    </div>
  );
}

export function ConflictResourceCard({
  result,
  requestedStart,
  requestedEnd,
  requiredSkills = [],
  isSelected = false,
  onToggle,
}: ConflictResourceCardProps) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div
      role="article"
      aria-label={`Resource: ${result.resource.name}, match score: ${result.overallScore}%, has conflict`}
      className={cn(
        'rounded-xl border transition-all duration-200 accent-warning',
        'hover:shadow-md hover:-translate-y-0.5',
        isSelected
          ? 'border-primary/40 bg-primary/[0.02]'
          : 'border-[var(--accent-border)] bg-[var(--accent-bg-outline)]/20'
      )}
    >
      {/* ── Identity + score (no action button here — it moves to after conflict details) ── */}
      <div className="px-4 pt-4 pb-3">
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <ResourceIdentityBlock
              resource={result.resource}
              size="lg"
              requiredSkills={requiredSkills}
            />
          </div>
          {/* Score badge only — action button sits below conflict details */}
          <div className="shrink-0 pt-0.5">
            <MatchScoreBadge score={result.overallScore} />
          </div>
        </div>

        {/* Availability */}
        <div className="mt-3">
          <AvailabilityWindow
            availablePeriods={result.availablePeriods}
            availabilityStatus={result.availabilityStatus}
            requestedStart={requestedStart}
            requestedEnd={requestedEnd}
            compact={false}
          />
        </div>
      </div>

      {/* ── Expandable conflict details ── */}
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className={cn(
          'w-full flex items-center justify-between px-4 py-2.5 text-xs text-muted-foreground',
          'border-t border-dashed border-[var(--accent-border)]/50 cursor-pointer',
          'hover:text-foreground hover:bg-muted/20 transition-all duration-150',
          expanded && 'text-foreground bg-muted/10'
        )}
        aria-label={expanded ? 'Hide conflict details' : 'Show conflict details'}
        aria-expanded={expanded}
      >
        <span className="font-semibold uppercase tracking-widest text-[10px]">
          Conflict details
        </span>
        {expanded ? (
          <ChevronUp className="h-3.5 w-3.5 shrink-0 transition-transform duration-150" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 transition-transform duration-150" />
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-0 pt-2 border-t space-y-4">
          {/* Conflict booking summary */}
          {result.conflictDetails && (
            <ConflictSummaryAlert conflictDetails={result.conflictDetails} />
          )}

          {/* Full signal breakdown */}
          {result.signals?.length > 0 && (
            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              {result.signals.map(s => (
                <SignalDetailRow key={s.key} signal={s} />
              ))}
            </div>
          )}

          {/* Impact warning banner — consequence-first, action second */}
          {result.conflictDetails?.impactSummary && (
            <div className="flex items-start gap-2.5 rounded-lg border border-amber-300/60 bg-amber-50/80 dark:bg-amber-950/30 dark:border-amber-700/40 px-3 py-2.5">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 dark:text-amber-300 leading-snug font-medium">
                {result.conflictDetails.impactSummary}
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── Override action — below conflict details so planner reads consequence first ── */}
      <div className={cn('px-4 pb-4', expanded ? 'pt-4' : 'pt-3')}>
        {isSelected ? (
          <button
            type="button"
            onClick={() => onToggle?.(result)}
            className="w-full flex items-center justify-center gap-1.5 text-xs text-primary font-medium px-2.5 py-2 rounded-lg bg-primary/10 hover:bg-primary/20 transition-colors cursor-pointer border border-primary/20"
            aria-label={`Deselect ${result.resource.name}`}
          >
            <CheckCircle className="h-3.5 w-3.5" />
            Added — click to remove
          </button>
        ) : (
          <Button
            type="button"
            variant="default"
            size="sm"
            className="w-full h-8 text-xs"
            onClick={() => onToggle?.(result)}
            aria-label={`Select ${result.resource.name} despite conflict`}
          >
            Override conflict
          </Button>
        )}
      </div>
    </div>
  );
}
